import { useState, useEffect, useRef } from "react";
import {
  Trail,
  TrailCategory,
  HikingTrail,
  isMantrailingTrail,
  isHikingTrail,
  isDetectionTrail,
  isCanicrossTrail,
} from "../../types/trail";
import { parseFeatureCollection } from "../../utils/gpx";
import {
  buildMantrailingPayload,
  buildDetectionPayload,
  buildHikingPayload,
  buildCanicrossPayload,
} from "../../utils/trail";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { RadioGroup, RadioGroupItem } from "../ui/radio-group";
import { X, Trash2 } from "lucide-react";
import {
  createHike,
  updateHike,
  saveTrail,
  updateTrail,
  deleteTrail,
  deleteHike,
  uploadHikePhoto,
  saveDetection,
  updateDetection,
  deleteDetection,
  saveCanicross,
  updateCanicross,
  deleteCanicross,
} from "../../utils/api";
import DogHomePageIcon from "../icon/DogHomePageIcon";
import HikeIcon from "../icon/HikeIcon";
import DetectionIcon from "../icon/DetectionIcon";
import CanicrossIcon from "../icon/CanicrossIcon";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "../ui/alert-dialog";
import { activityFormRegistry } from "./ActivityFormRegistry";
import { MantrailingFormHandle } from "./MantrailingForm";
import { HikingFormHandle } from "./HikingForm";
import { DetectionFormHandle } from "./DetectionForm";
import { CanicrossFormHandle } from "./CanicrossForm";
import { useAuth } from "../../contexts/AuthContext";
import { toast } from "sonner";

interface TrailFormProps {
  trail?: Trail;
  onSaveSuccess: () => void;
  onCancel: () => void;
}

function getApiFailureMessage(error: unknown): string {
  if (error && typeof error === "object") {
    const apiError = error as {
      response?: { status?: number; data?: { message?: unknown } | string };
      message?: string;
    };
    if (apiError.response?.status === 413) {
      return "La trace est trop volumineuse. Réduisez sa taille puis réessayez.";
    }
    if (apiError.response?.data && typeof apiError.response.data === "object" &&
        typeof apiError.response.data.message === "string") {
      return apiError.response.data.message;
    }
    if (!apiError.response && apiError.message === "Network Error") {
      return "Le serveur est injoignable. Vérifiez votre connexion puis réessayez.";
    }
  }
  return "Impossible d'enregistrer cette activité. Veuillez réessayer.";
}

export function TrailForm({ trail, onSaveSuccess, onCancel }: TrailFormProps) {
  const { user } = useAuth();
  const [category, setCategory] = useState<TrailCategory>("mantrailing");

  // Common fields only
  const [date, setDate] = useState(new Date().toISOString().split("T")[0]);
  const [location, setLocation] = useState("");
  const [, setLocationCoordinate] = useState<[number, number]>([
    46.2563,
    5.6554,
  ]);
  const [distance, setDistance] = useState(0);
  const [duration, setDuration] = useState(0);
  const [notes, setNotes] = useState("");

  // Refs for activity form components
  const mantrailingFormRef = useRef<MantrailingFormHandle>(null);
  const hikingFormRef = useRef<HikingFormHandle>(null);
  const detectionFormRef = useRef<DetectionFormHandle>(null);
  const canicrossFormRef = useRef<CanicrossFormHandle>(null);

  // Effect to populate form when trail prop changes
  useEffect(() => {
    if (!trail) {
      // Reset form
      setCategory("mantrailing");
      setDate(new Date().toISOString().split("T")[0]);
      setLocation("");
      setLocationCoordinate([46.2563, 5.6554]);
      setDistance(0);
      setDuration(0);
      setNotes("");
      return;
    }

    // Populate common fields
    setCategory(trail.category);
    setDate(
      trail.date instanceof Date
        ? trail.date.toISOString().split("T")[0]
        : trail.date || new Date().toISOString().split("T")[0],
    );
    setLocation(isCanicrossTrail(trail)
      ? trail.locationName || ""
      : typeof trail.location === "string" ? trail.location : "");
    setDistance(trail.distance || 0);
    setDuration(trail.duration || 0);
    setNotes(trail.notes || "");

    // Set location coordinate
    if (isMantrailingTrail(trail) && trail.locationCoordinate) {
      setLocationCoordinate(trail.locationCoordinate);
    } else if (isHikingTrail(trail) && trail.startLocation) {
      setLocationCoordinate([
        trail.startLocation.coordinates[1],
        trail.startLocation.coordinates[0],
      ]);
    }

    // Populate activity-specific fields via child form refs
    if (isMantrailingTrail(trail)) {
      const dogGpxData = parseFeatureCollection(trail.dogTrace);
      const userGpxData = parseFeatureCollection(trail.runnerTrace);

      mantrailingFormRef.current?.setData({
        selectedDogId: trail.dog?._id || "",
        handlerName: trail.handlerName || "",
        trainer: trail.trainer || "",
        trainerComment: trail.trainerComment || "",
        trailType: trail.trailType || "",
        startType: trail.startType === "blind" ? "blind" : "knowing",
        delay: trail.delay || 0,
        dogGpxData: dogGpxData
          ? { ...dogGpxData, distance: trail.distance }
          : null,
        userGpxData: userGpxData ? { ...userGpxData, distance: 0 } : null,
        locationCoordinate: trail.locationCoordinate || [46.2563, 5.6554],
      });
    }

    if (isDetectionTrail(trail)) {
      detectionFormRef.current?.setData({
        selectedDogId: trail.dog?._id || "",
        duration: trail.duration || 0,
        difficulty: trail.difficulty || "Moderate",
        targetScent: trail.targetScent || "",
        notes: trail.notes || "",
        trainer: trail.trainer || "",
        trainerComment: trail.trainerComment || "",
      });
    }

    if (isHikingTrail(trail)) {
      const userGpxData = parseFeatureCollection(trail.userTrack);
      const dogGpxData = parseFeatureCollection(trail.dogTrack);

      hikingFormRef.current?.setData({
        name: trail.name || "",
        description: trail.description || trail.notes || "",
        difficulty: trail.difficulty || "Moderate",
        elevationGain: trail.elevationGain || 0,
        avgSpeed: trail.avgSpeed,
        maxSpeed: trail.maxSpeed,
        avgHeartRate: trail.avgHeartRate,
        maxHeartRate: trail.maxHeartRate,
        selectedPhotos: [],
        existingPhotos: trail.photos || [],
        userGpxData: userGpxData
          ? { ...userGpxData, distance: trail.distance }
          : null,
        dogGpxData: dogGpxData ? { ...dogGpxData, distance: 0 } : null,
        locationCoordinate: trail.startLocation
          ? [
              trail.startLocation.coordinates[1],
              trail.startLocation.coordinates[0],
            ]
          : [46.2563, 5.6554],
      });
    }

  }, [trail]);

  useEffect(() => {
    if (!trail || category !== "canicross" || !isCanicrossTrail(trail)) return;

    const location = (trail as any).location;
    const coordinates = location?.coordinates
      ? [location.coordinates[1], location.coordinates[0]] as [number, number]
      : [46.2563, 5.6554] as [number, number];
    canicrossFormRef.current?.setData({
      selectedDogId: typeof trail.dogId === "string" ? trail.dogId : trail.dogId?._id || "",
      date: typeof trail.date === "string" ? trail.date.split("T")[0] : trail.date.toISOString().split("T")[0],
      distance: trail.distance || 0,
      duration: trail.duration || 0,
      avgSpeed: trail.avgSpeed || 0,
      maxSpeed: trail.maxSpeed || 0,
      avgPace: trail.avgPace || 0,
      maxPace: trail.maxPace,
      avgHeartRate: trail.avgHeartRate,
      maxHeartRate: trail.maxHeartRate,
      positiveElevation: trail.positiveElevation || 0,
      negativeElevation: trail.negativeElevation || 0,
      terrain: trail.terrain || "",
      pauseTime: trail.pauseTime || 0,
      energyLevel: trail.energyLevel || 0,
      notes: trail.notes || "",
      gpxData: parseFeatureCollection((trail as any).track),
      locationCoordinate: coordinates,
    });
  }, [category, trail]);

  useEffect(() => {
    if (!trail || category !== "hiking" || !isHikingTrail(trail)) return;

    const userGpxData = parseFeatureCollection(trail.userTrack);
    const dogGpxData = parseFeatureCollection(trail.dogTrack);

    hikingFormRef.current?.setData({
      name: trail.name || "",
      description: trail.description || trail.notes || "",
      difficulty: trail.difficulty || "Moderate",
      elevationGain: trail.elevationGain || 0,
      avgSpeed: trail.avgSpeed,
      maxSpeed: trail.maxSpeed,
      avgPace: trail.avgPace,
      maxPace: trail.maxPace,
      avgHeartRate: trail.avgHeartRate,
      maxHeartRate: trail.maxHeartRate,
      selectedPhotos: [],
      existingPhotos: trail.photos || [],
      userGpxData: userGpxData
        ? { ...userGpxData, distance: trail.distance }
        : null,
      dogGpxData: dogGpxData ? { ...dogGpxData, distance: 0 } : null,
      locationCoordinate: trail.startLocation
        ? [
            trail.startLocation.coordinates[1],
            trail.startLocation.coordinates[0],
          ]
        : [46.2563, 5.6554],
    });
  }, [category, trail]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    try {
      if (category === "mantrailing") {
        const formData = mantrailingFormRef.current?.getData();
        if (!formData) return;

        const mantrailingData = buildMantrailingPayload({
          ...formData,
          date,
          location,
          distance,
          duration,
          notes,
        });

        if (trail && (trail.id || trail._id)) {
          await updateTrail(trail.id || trail._id || "", mantrailingData);
        } else {
          await saveTrail(mantrailingData);
        }
      } else if (category === "detection") {
        const formData = detectionFormRef.current?.getData();
        if (!formData) return;

        const detectionData = buildDetectionPayload({
          ...formData,
          date,
          location,
        });

        if (trail && (trail.id || trail._id)) {
          await updateDetection(trail.id || trail._id || "", detectionData);
        } else {
          await saveDetection(detectionData);
        }
      } else if (category === "canicross") {
        const formData = canicrossFormRef.current?.getData();
        if (!formData) return;
        const canicrossData = buildCanicrossPayload({ ...formData, date, location });
        if (trail && (trail.id || trail._id)) {
          await updateCanicross(trail.id || trail._id || "", canicrossData);
        } else {
          await saveCanicross(canicrossData);
        }
      } else {
        const formData = hikingFormRef.current?.getData();
        if (!formData) return;

        const hikingData = buildHikingPayload({
          ...formData,
          date,
          distance,
          duration,
        });

        let currentHikeId = trail?.id || trail?._id;
        let savedHike;

        if (currentHikeId) {
          const initialPhotos =
            trail && isHikingTrail(trail) && trail.photos ? trail.photos : [];
          if (
            JSON.stringify(formData.existingPhotos) ===
            JSON.stringify(initialPhotos)
          ) {
            delete hikingData.photos;
          }
          savedHike = await updateHike(
            currentHikeId,
            hikingData as Omit<HikingTrail, "id" | "_id">,
          );
          currentHikeId = savedHike._id || savedHike.id;
        } else {
          savedHike = await createHike(hikingData as HikingTrail);
          currentHikeId = savedHike._id || savedHike.id;
        }

        if (formData.selectedPhotos.length > 0 && currentHikeId) {
          for (const photo of formData.selectedPhotos) {
            await uploadHikePhoto(currentHikeId, photo);
          }
        }
      }
      onSaveSuccess();
    } catch (error) {
      console.error("Failed to save trail:", error);
      toast.error(getApiFailureMessage(error));
    }
  };

  const handleDelete = async () => {
    if (!trail) return;

    try {
      if (category === "mantrailing") {
        await deleteTrail(trail.id || trail._id || "");
      } else if (category === "detection") {
        await deleteDetection(trail.id || trail._id || "");
      } else if (category === "canicross") {
        await deleteCanicross(trail.id || trail._id || "");
      } else {
        await deleteHike(trail.id || trail._id || "");
      }
      onSaveSuccess();
    } catch (error) {
      console.error("Failed to delete trail:", error);
      toast.error(getApiFailureMessage(error));
    }
  };

  const ActivityForm = activityFormRegistry[category];

  const getFormRef = () => {
    if (category === "mantrailing") return mantrailingFormRef;
    if (category === "hiking") return hikingFormRef;
    if (category === "detection") return detectionFormRef;
    if (category === "canicross") return canicrossFormRef;
    return null;
  };

  return (
    <div className="h-full w-full overflow-auto bg-gradient-to-br from-blue-50 to-white">
      <div className="mx-auto w-full max-w-4xl p-3 sm:p-6">
        <Card className="border-2 border-blue-200 shadow-xl">
          <CardHeader className="bg-gradient-to-r from-blue-100 to-green-100">
            <div className="flex items-center justify-between">
              <CardTitle className="flex min-w-0 items-center gap-2 text-base text-blue-900 sm:text-xl">
                {category === "mantrailing" ? (
                  <DogHomePageIcon className="h-6 w-6" />
                ) : category === "hiking" ? (
                  <HikeIcon className="h-6 w-6" />
                ) : category === "detection" ? (
                  <DetectionIcon className="h-6 w-6" />
                ) : (
                  <CanicrossIcon className="h-6 w-6" />
                )}
                {trail ? "Modifier" : "Nouvelle"}{" "}
                {category === "mantrailing"
                  ? "piste de mantrailing"
                  : category === "hiking"
                    ? "randonnée"
                    : category === "detection"
                      ? "session de détection"
                      : "session de canicross"}
              </CardTitle>
              <Button variant="ghost" size="icon" onClick={onCancel}>
                <X className="h-5 w-5" />
              </Button>
            </div>
          </CardHeader>

          <CardContent className="p-3 sm:p-6">
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Category Selection */}
              <div className="space-y-3">
                <Label className="text-blue-900">Type d'activité *</Label>
                <RadioGroup
                  value={category}
                  onValueChange={(value) => setCategory(value as TrailCategory)}
                >
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div
                      className={`flex items-center space-x-2 border-2 rounded-lg p-4 cursor-pointer transition-all ${
                        category === "mantrailing"
                          ? "border-blue-500 bg-blue-50"
                          : "border-gray-200 hover:border-blue-300"
                      }`}
                    >
                      <RadioGroupItem value="mantrailing" id="mantrailing" />
                      <label
                        htmlFor="mantrailing"
                        className="flex items-center gap-2 cursor-pointer flex-1"
                      >
                        <DogHomePageIcon className="h-5 w-5 text-blue-600" />
                        <span>Mantrailing</span>
                      </label>
                    </div>
                    <div
                      className={`flex items-center space-x-2 border-2 rounded-lg p-4 cursor-pointer transition-all ${
                        category === "canicross"
                          ? "border-orange-500 bg-orange-50"
                          : "border-gray-200 hover:border-orange-300"
                      }`}
                    >
                      <RadioGroupItem value="canicross" id="canicross" />
                      <label htmlFor="canicross" className="flex items-center gap-2 cursor-pointer flex-1">
                        <CanicrossIcon className="h-5 w-5 text-orange-600" />
                        <span>Canicross</span>
                      </label>
                    </div>
                    <div
                      className={`flex items-center space-x-2 border-2 rounded-lg p-4 cursor-pointer transition-all ${
                        category === "hiking"
                          ? "border-green-500 bg-green-50"
                          : "border-gray-200 hover:border-green-300"
                      }`}
                    >
                      <RadioGroupItem value="hiking" id="hiking" />
                      <label
                        htmlFor="hiking"
                        className="flex items-center gap-2 cursor-pointer flex-1"
                      >
                        <HikeIcon className="h-5 w-5 text-green-600" />
                        <span>Randonnée</span>
                      </label>
                    </div>
                    <div
                      className={`flex items-center space-x-2 border-2 rounded-lg p-4 cursor-pointer transition-all ${
                        category === "detection"
                          ? "border-purple-500 bg-purple-50"
                          : "border-gray-200 hover:border-purple-300"
                      }`}
                    >
                      <RadioGroupItem value="detection" id="detection" />
                      <label
                        htmlFor="detection"
                        className="flex items-center gap-2 cursor-pointer flex-1"
                      >
                        <DetectionIcon className="h-5 w-5 text-purple-600" />
                        <span>Détection</span>
                      </label>
                    </div>
                  </div>
                </RadioGroup>
              </div>

              {/* Common Fields */}
              <div className="space-y-4">
                <h3 className="text-lg text-blue-900">
                  Informations générales
                </h3>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="date">Date *</Label>
                    <Input
                      id="date"
                      type="date"
                      value={date.split("T")[0]}
                      onChange={(e) => setDate(e.target.value)}
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="location">Lieu</Label>
                    <Input
                      id="location"
                      value={location}
                      onChange={(e) => setLocation(e.target.value)}
                      placeholder="Ex: Oyonnax"
                    />
                  </div>
                </div>
              </div>

              {/* Activity Form with ref */}
              {ActivityForm && (
                <ActivityForm
                  ref={getFormRef()}
                  user={user}
                  dogsPlaceholder={
                    trail && isMantrailingTrail(trail) ? [trail.dog] : undefined
                  }
                  location={location}
                  setLocation={setLocation}
                  onDistanceChange={setDistance}
                  onDurationChange={setDuration}
                  onLocationCoordinateChange={setLocationCoordinate}
                  onDateChange={setDate}
                />
              )}

              {/* Activity Specific Fields - only for non-detection */}
              {category !== "detection" && category !== "canicross" && (
                <div className="space-y-4">
                  <h3 className="text-lg text-blue-900">
                    Détails de la session
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="distance">Distance (m)</Label>
                      <Input
                        id="distance"
                        type="number"
                        value={distance}
                        onChange={(e) => setDistance(Number(e.target.value))}
                        placeholder="0"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="duration">Durée (secondes)</Label>
                      <Input
                        id="duration"
                        type="number"
                        value={duration}
                        onChange={(e) => setDuration(Number(e.target.value))}
                        placeholder="0"
                      />
                    </div>
                  </div>

                  {category !== "hiking" && (
                    <div className="space-y-2">
                      <Label htmlFor="notes">Notes</Label>
                      <Textarea
                        id="notes"
                        value={notes}
                        onChange={(e) => setNotes(e.target.value)}
                        placeholder="Ajoutez vos observations..."
                        rows={4}
                      />
                    </div>
                  )}
                </div>
              )}

              {/* Form Actions */}
              <div className="flex gap-3 pt-4">
                <Button
                  type="submit"
                  className="flex-1 bg-blue-600 hover:bg-blue-700"
                >
                  Enregistrer
                </Button>
                <Button type="button" variant="outline" onClick={onCancel}>
                  Annuler
                </Button>
                {trail && (
                  <AlertDialog>
                    <AlertDialogTrigger asChild>
                      <Button
                        type="button"
                        variant="destructive"
                        className="bg-red-600 hover:bg-red-700"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </AlertDialogTrigger>
                    <AlertDialogContent>
                      <AlertDialogHeader>
                        <AlertDialogTitle>
                          Êtes-vous sûr de vouloir supprimer cette trace ?
                        </AlertDialogTitle>
                        <AlertDialogDescription>
                          Cette action est irréversible.
                        </AlertDialogDescription>
                      </AlertDialogHeader>
                      <AlertDialogFooter>
                        <AlertDialogCancel>Annuler</AlertDialogCancel>
                        <AlertDialogAction
                          onClick={handleDelete}
                          className="bg-red-600 hover:bg-red-700"
                        >
                          Supprimer
                        </AlertDialogAction>
                      </AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>
                )}
              </div>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
